import { useState } from "react";
import type { NotificationDto } from "@/lib/notifications";

export interface NotificationBellProps {
  notifications: NotificationDto[];
  onItemClick: (n: NotificationDto) => void;
  onMarkAllRead: () => void;
}

export function NotificationBell({ notifications, onItemClick, onMarkAllRead }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const count = notifications.length;

  return (
    <div className="relative inline-block" data-testid="notification-bell">
      <button
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        className="relative rounded-full p-2 hover:bg-secondary"
        onClick={() => setOpen((v) => !v)}
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground"
            data-testid="notification-badge">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-72 rounded-lg border border-border bg-popover shadow-md" data-testid="notification-list">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-medium">Notifications</span>
            {count > 0 && (
              <button type="button" className="text-xs text-primary hover:underline" onClick={onMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No notifications</p>
          ) : (
            <ul className="divide-y">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col items-start gap-0.5 p-3 text-left hover:bg-secondary"
                    onClick={() => { setOpen(false); onItemClick(n); }}
                  >
                    <span className="truncate text-sm font-medium">{n.title}</span>
                    {n.body && <span className="truncate text-xs text-muted-foreground">{n.body}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
